// src/support/aiApi.ts

import { buildApiUrl, getApiHeaders } from './queriesHelpers';

/**
 * An error from an API response, carrying the HTTP status so callers (and react-query's retry
 * predicate) can tell a 4xx — which must NOT be retried (retrying a 429 only deepens the rate
 * limit; retrying a 401/403/404 will never succeed) — from a transient 5xx/network failure.
 */
export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

export const buildQueryString = (params?: Record<string, any>) => {
  if (!params) return '';

  const entries = Object.entries(params).filter(
    ([, value]) => value !== undefined && value !== null && value !== ''
  );

  if (!entries.length) return '';

  return `?${entries
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`
    )
    .join('&')}`;
};

async function parseError(resp: Response, fallback: string) {
  try {
    const data = await resp.json();
    return data?.error || data?.detail || data?.message || fallback;
  } catch {
    return fallback;
  }
}

export async function apiGet<T = any>(
  endpoint: string,
  params?: Record<string, any>
): Promise<T> {
  const resp = await fetch(buildApiUrl(endpoint) + buildQueryString(params), {
    method: 'GET',
    headers: getApiHeaders(null, 'GET', endpoint),
    credentials: 'include',
  });

  if (!resp.ok) {
    throw new ApiError(await parseError(resp, `GET ${endpoint} failed`), resp.status);
  }

  return resp.json();
}

export async function apiPostJson<T = any>(
  endpoint: string,
  body?: any
): Promise<T> {
  const resp = await fetch(buildApiUrl(endpoint), {
    method: 'POST',
    headers: getApiHeaders(null, 'POST', endpoint),
    body: JSON.stringify(body ?? {}),
    credentials: 'include',
  });

  if (!resp.ok) {
    throw new ApiError(await parseError(resp, `POST ${endpoint} failed`), resp.status);
  }

  return resp.json();
}

export async function apiPutJson<T = any>(
  endpoint: string,
  body?: any
): Promise<T> {
  const resp = await fetch(buildApiUrl(endpoint), {
    method: 'PUT',
    headers: getApiHeaders(null, 'PUT', endpoint),
    body: JSON.stringify(body ?? {}),
    credentials: 'include',
  });

  if (!resp.ok) {
    throw new ApiError(await parseError(resp, `PUT ${endpoint} failed`), resp.status);
  }

  return resp.json();
}

export async function apiDelete<T = any>(
  endpoint: string,
  params?: Record<string, any>
): Promise<T> {
  const resp = await fetch(buildApiUrl(endpoint) + buildQueryString(params), {
    method: 'DELETE',
    headers: getApiHeaders(null, 'DELETE', endpoint),
    credentials: 'include',
  });

  if (!resp.ok) {
    throw new ApiError(await parseError(resp, `DELETE ${endpoint} failed`), resp.status);
  }

  const text = await resp.text();

  if (!text) return { ok: true } as T;

  try {
    return JSON.parse(text);
  } catch {
    return { ok: true } as T;
  }
}

export async function apiPostForm<T = any>(
  endpoint: string,
  form: FormData
): Promise<T> {
  const headers = { ...getApiHeaders(null, 'POST', endpoint) } as Record<
    string,
    string
  >;

  delete headers['Content-Type'];

  const resp = await fetch(buildApiUrl(endpoint), {
    method: 'POST',
    headers,
    body: form,
    credentials: 'include',
  });

  if (!resp.ok) {
    throw new ApiError(await parseError(resp, `POST ${endpoint} failed`), resp.status);
  }

  return resp.json();
}

export function toItems<T = any>(resp: any): T[] {
  if (Array.isArray(resp)) return resp;
  if (Array.isArray(resp?.items)) return resp.items;
  if (Array.isArray(resp?.data)) return resp.data;
  if (Array.isArray(resp?.results)) return resp.results;
  return [];
}